'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useUserRole } from '@/components/dashboard/hooks/useUserRole';
import React from 'react';
import { School } from '@/types/school';

// Import all sections
import DefaultHero1 from '@/components/templates/sections/DefaultHero1';
import StatsSection from '@/components/templates/sections/StatsSection';
import CategoriesSection from '@/components/templates/sections/CategoriesSection';
import FeaturedCoursesSection from '@/components/templates/sections/FeaturedCoursesSection';
import AboutSection from '@/components/templates/sections/AboutSection';
import TestimonialsSection from '@/components/templates/sections/TestimonialsSection';
import BlogSection from '@/components/templates/sections/BlogSection';
import DefaultFooter from '@/components/templates/sections/DefaultFooter';

export default function HomePage() {
  const { role, ready, userId } = useUserRole();
  const router = useRouter();

  // Redirect logged-in users to dashboard
  useEffect(() => {
    if (ready && userId) {
      router.push('/dashboard');
    }
  }, [ready, userId, router]);

  // Show loading while checking auth status
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // If user is logged in, show loading (will redirect)
  if (userId) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Redirecting to dashboard...</p>
        </div>
      </div>
    );
  }

  // Mock school data
  const mockSchool: School = {
    id: '1',
    name: 'ScoreEze Learning Platform',
    domain: 'scoreeze.example.com',
    slug: 'scoreeze',
    email: 'info@scoreeze.com',
    phone: '+1 234 567 8900',
    address: '123 Education Street, Learning City',
    logo: 'https://via.placeholder.com/150',
    favicon: 'https://via.placeholder.com/32',
    primaryColor: '#3b82f6',
    secondaryColor: '#1e40af',
    accentColor: '#60a5fa',
    seoTitle: 'ScoreEze - Online Learning Platform',
    seoDescription: 'Learn from anywhere with our comprehensive online courses',
    siteTemplate: 'default',
    canReceiveSharedContent: true,
    canCreateOwnContent: true,
    defaultLanguage: 'en',
    supportedLanguages: ['en'],
    subscriptionStatus: 'active',
    subscriptionPlan: 'premium',
    studentLimit: 1000,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  // Mock template data
  const mockTemplate = {
    id: '1',
    name: 'default',
    displayName: 'Default Template',
    colors: {
      primary: mockSchool.primaryColor,
      secondary: mockSchool.secondaryColor,
      accent: mockSchool.accentColor,
      background: '#ffffff',
      text: '#1f2937',
    },
    layout: 'standard',
    category: 'education',
  };

  // Mock section data
  const mockSection = {
    id: 'section-1',
    sectionType: 'hero',
    sectionName: 'Hero Section',
    component: 'DefaultHero',
    isEnabled: true,
    sortOrder: 1,
    config: {},
    styling: {},
  };

  // Mock config and customization
  const mockConfig = {};
  const mockCustomization = {};
  const mockContent = {};

  // Show landing page for non-logged-in users
  // DefaultHero1 already includes all sections, so we hide the duplicate sections below
  // Uncomment the sections below if you want to use them separately instead of DefaultHero1
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Using DefaultHero1 as it's a full homepage component */}
      <section className="hero-section">
        <DefaultHero1 />
      </section>

      {/* HIDDEN SECTIONS - Uncomment below to use individual sections instead of DefaultHero1 */}
      {/* 
      <StatsSection
        school={mockSchool}
        template={mockTemplate}
        section={{ ...mockSection, sectionType: 'stats', sectionName: 'Statistics' }}
        config={mockConfig}
        styling={{}}
        customization={mockCustomization}
        content={mockContent}
      />

      <CategoriesSection
        school={mockSchool}
        template={mockTemplate}
        section={{ ...mockSection, sectionType: 'categories', sectionName: 'Categories' }}
        config={mockConfig}
        styling={{}}
        customization={mockCustomization}
        content={mockContent}
      />

      <FeaturedCoursesSection
        school={mockSchool}
        template={mockTemplate}
        section={{ ...mockSection, sectionType: 'courses', sectionName: 'Featured Courses' }}
        config={mockConfig}
        styling={{}}
        customization={mockCustomization}
        content={mockContent}
      />

      <AboutSection
        school={mockSchool}
        template={mockTemplate}
        section={{ ...mockSection, sectionType: 'about', sectionName: 'About Us' }}
        config={mockConfig}
        styling={{}}
        customization={mockCustomization}
        content={mockContent}
      />

      <TestimonialsSection
        school={mockSchool}
        template={mockTemplate}
        section={{ ...mockSection, sectionType: 'testimonials', sectionName: 'Testimonials' }}
        config={mockConfig}
        styling={{}}
        customization={mockCustomization}
        content={mockContent}
      />

      <BlogSection
        school={mockSchool}
        template={mockTemplate}
        section={{ ...mockSection, sectionType: 'blog', sectionName: 'Blog Posts' }}
        config={mockConfig}
        styling={{}}
        customization={mockCustomization}
        content={mockContent}
      />

      <DefaultFooter
        school={mockSchool}
        template={mockTemplate}
        section={{ ...mockSection, sectionType: 'footer', sectionName: 'Footer' }}
        config={mockConfig}
        styling={{}}
        customization={mockCustomization}
        content={mockContent}
      />
      */}
    </div>
  );
}